import { useEffect, useRef } from 'react';

interface ThreeDSModalProps {
  isOpen: boolean;
  stepUpUrl: string;
  accessToken: string; // Long JWT from check_enrollment consumerAuthenticationInformation.accessToken
  /** The authenticationTransactionId from check_enrollment — used as the Cardinal TransactionId */
  authTransactionId: string;
  onAuthenticationComplete: (transactionId: string) => void;
  onError: (error: string) => void;
}

/**
 * 3DS Step-Up Authentication Modal
 *
 * Two parallel mechanisms detect challenge completion:
 *
 * PRIMARY — Same-origin iframe detection:
 *   After the user completes the challenge, Cardinal navigates the step-up iframe
 *   FROM its cross-origin domain TO our returnUrl (same-origin with the parent).
 *   The iframe's onload fires again; we try to read iframe.contentWindow.location.
 *   If it succeeds (same-origin), we know the challenge is done.
 *   We already have the authTransactionId from check_enrollment — it IS the
 *   same TransactionId Cardinal would POST to the returnUrl.
 *
 * SECONDARY — postMessage from returnUrl:
 *   If our server-side endpoint (Vite middleware in dev / Lambda in prod) handles
 *   Cardinal's POST and returns HTML that calls window.parent.postMessage, we also
 *   listen for data.TransactionId.
 *
 * Whichever fires first wins.
 */
export const ThreeDSModal = ({
  isOpen,
  stepUpUrl,
  accessToken,
  authTransactionId,
  onAuthenticationComplete,
  onError,
}: ThreeDSModalProps) => {
  const submittedRef = useRef(false);
  const openedRef = useRef(false);
  const completedRef = useRef(false); // prevent double-calling onAuthenticationComplete

  // ── Shared completion handler (idempotent) ───────────────────────────────
  const completeAuth = (tid: string, source: string) => {
    if (completedRef.current) return;
    completedRef.current = true;
    console.log(`[ThreeDSModal] Auth complete via ${source}. TransactionId: ${tid}`);
    removeModal();
    submittedRef.current = false;
    onAuthenticationComplete(tid);
  };

  // ── SECONDARY: Listen for postMessage from the returnUrl page ─────────────
  useEffect(() => {
    if (!isOpen) return;

    let timeout: ReturnType<typeof setTimeout>;

    const messageHandler = (event: MessageEvent) => {
      try {
        let data: any;
        if (typeof event.data === 'string') {
          try { data = JSON.parse(event.data); } catch { return; }
        } else {
          data = event.data;
        }
        if (data && data.TransactionId) {
          clearTimeout(timeout);
          window.removeEventListener('message', messageHandler);
          completeAuth(data.TransactionId, 'postMessage');
        }
      } catch { /* ignore */ }
    };

    window.addEventListener('message', messageHandler);

    // 5-minute timeout
    timeout = setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      removeModal();
      submittedRef.current = false;
      completedRef.current = false;
      onError('Authentication timed out. Please try again.');
    }, 300000);

    return () => {
      window.removeEventListener('message', messageHandler);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Build modal + iframe + form using vanilla DOM ────────────────────────
  useEffect(() => {
    if (!isOpen || !stepUpUrl || !accessToken) return;

    if (submittedRef.current) {
      console.log('[ThreeDSModal] Step-up already submitted — skipping (Strict Mode guard)');
      return;
    }
    submittedRef.current = true;
    openedRef.current = true;
    completedRef.current = false;

    // ── Backdrop ─────────────────────────────────────────────────────────────
    const backdrop = document.createElement('div');
    backdrop.id = 'threeds-modal-backdrop';
    backdrop.setAttribute('data-no-focus-lock', 'true');
    backdrop.setAttribute('data-focus-lock-disabled', 'true');
    Object.assign(backdrop.style, {
      position: 'fixed', top: '0', left: '0',
      width: '100vw', height: '100vh', zIndex: '10002',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.55)',
    });

    const focusGuard = (e: FocusEvent) => {
      if (backdrop.contains(e.target as Node) || e.target === document.body) {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('focusin', focusGuard, true);
    document.addEventListener('focusout', focusGuard, true);
    (backdrop as any).__focusGuard = focusGuard;

    // ── Dialog ───────────────────────────────────────────────────────────────
    const dialog = document.createElement('div');
    dialog.setAttribute('data-no-focus-lock', 'true');
    Object.assign(dialog.style, {
      background: 'white', borderRadius: '16px',
      width: '480px', maxWidth: '94vw',
      boxShadow: '0 24px 64px rgba(0,0,0,0.35)', padding: '24px',
      boxSizing: 'border-box',
    });

    const title = Object.assign(document.createElement('h3'), {
      textContent: '3D Secure Authentication'
    });
    Object.assign(title.style, {
      margin: '0 0 6px 0', fontSize: '18px', color: '#111', textAlign: 'center'
    });

    const sub = Object.assign(document.createElement('p'), {
      textContent: 'Complete the authentication from your bank below'
    });
    Object.assign(sub.style, {
      fontSize: '13px', color: '#888', margin: '0 0 14px 0', textAlign: 'center'
    });

    const spinner = document.createElement('div');
    spinner.id = 'threeds-spinner';
    Object.assign(spinner.style, {
      textAlign: 'center', padding: '20px', color: '#666', fontSize: '14px'
    });
    spinner.textContent = 'Loading bank authentication…';

    // ── iframe ───────────────────────────────────────────────────────────────
    const iframe = document.createElement('iframe');
    iframe.setAttribute('name', 'step-up-iframe');
    iframe.setAttribute('title', '3DS Step-Up Authentication');
    iframe.setAttribute('height', '400');
    iframe.setAttribute('width', '432');
    iframe.setAttribute('data-no-focus-lock', 'true');
    iframe.setAttribute('tabindex', '-1');
    Object.assign(iframe.style, {
      border: '1px solid #eee', borderRadius: '8px',
      background: '#f9f9f9', display: 'block', width: '100%',
    });

    let iframeLoadCount = 0;

    iframe.addEventListener('load', () => {
      iframeLoadCount++;
      const s = document.getElementById('threeds-spinner');
      if (s) s.style.display = 'none';

      // PRIMARY mechanism: detect when Cardinal navigates the step-up iframe
      // FROM Cardinal's cross-origin domain BACK to our domain (same-origin).
      // Load #1 = Cardinal challenge page (cross-origin, will throw on location access)
      // Load #2+ = Our returnUrl page (same-origin, location access succeeds)
      if (iframeLoadCount >= 2) {
        try {
          // If this doesn't throw, we are now same-origin (our returnUrl domain)
          const href = iframe.contentWindow?.location?.href ?? '';
          if (href && new URL(href).origin === window.location.origin) {
            console.log('[ThreeDSModal] Same-origin iframe load detected — challenge complete. URL:', href);
            // Use the authTransactionId we already have from check_enrollment.
            // This is the same ID Cardinal would POST to the returnUrl.
            completeAuth(authTransactionId, 'same-origin iframe');
          }
        } catch {
          // Still cross-origin — ignore
          console.log('[ThreeDSModal] Load #' + iframeLoadCount + ' still cross-origin, waiting...');
        }
      }
    });

    dialog.appendChild(title);
    dialog.appendChild(sub);
    dialog.appendChild(spinner);
    dialog.appendChild(iframe);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // ── Form POST to step-up URL, targeting the iframe ───────────────────────
    const form = document.createElement('form');
    form.setAttribute('method', 'POST');
    form.setAttribute('action', stepUpUrl);
    form.setAttribute('target', 'step-up-iframe');
    const jwtInput = document.createElement('input');
    jwtInput.setAttribute('type', 'hidden');
    jwtInput.setAttribute('name', 'JWT');
    jwtInput.setAttribute('value', accessToken);
    form.appendChild(jwtInput);
    document.body.appendChild(form);

    console.log('[ThreeDSModal] Submitting step-up form to:', stepUpUrl, '| JWT length:', accessToken.length);
    form.submit();
    setTimeout(() => { if (document.body.contains(form)) form.remove(); }, 500);

    return () => {
      // Do NOT reset submittedRef here — Strict Mode calls cleanup then re-runs.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, stepUpUrl, accessToken]);

  // ── Cleanup when isOpen → false ──────────────────────────────────────────
  useEffect(() => {
    if (!isOpen && openedRef.current) {
      openedRef.current = false;
      submittedRef.current = false;
      completedRef.current = false;
      removeModal();
    }
  }, [isOpen]);

  return null;
};

function removeModal() {
  const el = document.getElementById('threeds-modal-backdrop');
  if (el) {
    const guard = (el as any).__focusGuard;
    if (guard) {
      document.removeEventListener('focusin', guard, true);
      document.removeEventListener('focusout', guard, true);
    }
    if (el.parentNode) el.parentNode.removeChild(el);
  }
}
